/**
 * Onboarding Store
 *
 * Manages the state of the guided tour for new users.
 * Persists completion status to localStorage.
 */

// Storage key for localStorage persistence
const ONBOARDING_STORAGE_KEY = 'photodrop_onboarding'

export interface OnboardingState {
  tourCompleted: boolean
  currentStep: number
  completedAt?: string
}

// Tour step configuration
export interface TourStep {
  id: string
  title: string
  description: string
  targetSelector?: string // CSS selector for element to highlight
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center'
}

// Define the tour steps
export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Photodrop!',
    description: 'Let us show you around. This quick tour will help you get started with collecting and sharing photos.',
    placement: 'center',
  },
  {
    id: 'create-drop',
    title: 'Create Your First Drop',
    description: 'A "drop" is a collection where your guests can upload photos. Click the "New Drop" button to create one for your event.',
    targetSelector: '[data-tour="new-drop-button"]',
    placement: 'bottom',
  },
  {
    id: 'invite-contributors',
    title: 'Invite Contributors',
    description: 'Once you create a drop, share the link with your guests. They can upload photos without creating an account!',
    targetSelector: '[data-tour="share-button"]',
    placement: 'bottom',
  },
  {
    id: 'view-gallery',
    title: 'View Your Gallery',
    description: 'All uploaded photos appear in your gallery. You can view, download, and share them with everyone.',
    targetSelector: '[data-tour="gallery-tab"]',
    placement: 'bottom',
  },
  {
    id: 'complete',
    title: 'You\'re All Set!',
    description: 'That\'s all you need to know to get started. Create your first drop and start collecting memories!',
    placement: 'center',
  },
]

// Get onboarding state from localStorage
export function getOnboardingState(): OnboardingState {
  const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY)
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch {
      // Return default state if parse fails
    }
  }
  return {
    tourCompleted: false,
    currentStep: 0,
  }
}

// Save onboarding state to localStorage
export function saveOnboardingState(state: OnboardingState): void {
  localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state))
}

// Check if tour should be shown
export function shouldShowTour(): boolean {
  const state = getOnboardingState()
  return !state.tourCompleted
}

// Advance to next step
export function advanceToNextStep(): number {
  const state = getOnboardingState()
  const nextStep = state.currentStep + 1
  saveOnboardingState({
    ...state,
    currentStep: nextStep,
  })
  return nextStep
}

// Go to previous step
export function goToPreviousStep(): number {
  const state = getOnboardingState()
  const prevStep = Math.max(0, state.currentStep - 1)
  saveOnboardingState({
    ...state,
    currentStep: prevStep,
  })
  return prevStep
}

// Mark tour as completed
export function completeTour(): void {
  saveOnboardingState({
    tourCompleted: true,
    currentStep: TOUR_STEPS.length - 1,
    completedAt: new Date().toISOString(),
  })
}

// Skip tour
export function skipTour(): void {
  completeTour()
}

// Reset tour (for testing)
export function resetTour(): void {
  localStorage.removeItem(ONBOARDING_STORAGE_KEY)
}

// Get current step
export function getCurrentStep(): number {
  return getOnboardingState().currentStep
}
